import React, {useEffect, useState} from "react";
import {
    Grid,
    Typography,
    Button,
    Avatar,
    Box,
    Container,
    GridItemsAlignment,
    Card
} from "@material-ui/core";
import {ReactComponent as WarningSVG} from "../../Images/warning.svg";
import {useHistory, useLocation} from "react-router-dom";
import {makeStyles, Theme, useTheme} from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import {useSnackbar} from "notistack";

import {ServiceType, Navigation} from "../../Components";
import {statesWithoutPrescription} from "../../Constants/locations";
import {
    adviceServices,
    RX_Services,
    adviceServiceAtlanta,
    reasons,
    setAppointmentPrice,
} from "../../Constants/serviceType";
import {colorSecondary, colorWhite} from "../../Constants/theme";
import {ReactComponent as PetCatSVG} from "../../Images/petCat.svg";
import {ReactComponent as PetDogSVG} from "../../Images/petDogNew.svg";
import {ReactComponent as LogoSVG} from "../../Images/logo.svg";
import Image from "material-ui-image";
import API from "../../helpers/apiHelpers";
import {serviceType, reasonType, immediateType, desiredDates} from "../../interfaces";
import {useSelector} from "react-redux";
import {RootState, useAppDispatch} from "../../redux/store";
import {getCreditRequestsCount, getCreditsExpirationDate} from "../../redux/credits/credits.actions";
import {getAddress, getUserProfile, patchAddress} from "../../redux/userProfile/userProfile.actions";
import {setPrice} from "../../redux/scheduleAppointment/scheduling.reducers";
import {
    cleanUpPhotos,
    cleanUpVideo,
    updateIsImmediate,
    updatePaymentMethod,
    updateRequestReason,
    updateSelectedPetID,
    updateSelectedState,
    updateServiceType, updateVetId, updateVetProfile,
} from "../../redux/scheduleAppointment/scheduling.actions";
import {setPetType} from "../../utils/petInfo";
import {api_getLastAppointment} from "../../redux/scheduleAppointment/scheduling.api";
import {api_getUserProfile} from "../../redux/userProfile/userProfile.api";
import withLocation from "../../hocs/withLocation";
import BookAgainCard from "../../Components/BookAgainCard";
import {useUpdateUpcomingAppts} from "../../hooks/useUpdateUpcomingAppts";
import {setClientPrices, setLocation, setChangesForSession, setSelectedPrice} from "../../redux/common/common.reducers";

const useStyle = makeStyles((theme: Theme) => ({
    wrapper: {
        height: "100%",
        marginTop: 90,
        marginBottom: 75
    },
    rowContainer: {
        marginTop: "20px",
        padding: 10,
    },
    input: {
        width: "100%"
    },
    spanContainer: {
        fontFamily: "DM Sans",
        fontStyle: "normal",
        fontWeight: "bold",
        fontSize: "24px",
        lineHeight: "26px"
    },
    spanOptions: {
        fontFamily: "DM Sans",
        fontStyle: "normal",
        fontWeight: "bold",
        fontSize: "18px",
        lineHeight: "26px",
        marginBottom: 10,
        width: 100
    },
    avatar: {
        width: 95,
        height: 95,
        cursor: "pointer",
        backgroundColor: colorWhite
    },
    addPet: {
        color: colorSecondary,
        cursor: "pointer"
    },
    dropZone: {
        marginRight: 10,
        marginBottom: 10,
    },
    comentarWidth: {
        width: "100%",
        [theme.breakpoints.down("sm")]: {
            width: "auto"
        }
    },
    nextButton: {
        width: "100%",
        maxWidth: "322px",
        [theme.breakpoints.down("sm")]: {
            width: "100%"
        },
    },
    selectedPet: {
        boxShadow: `0 0 0 3px ${theme.palette.primary.main}`,
        borderRadius: 5,
    },
    avatar__addNew: {
        marginTop: 10,
    },
    sectionText: {
        textAlign: "center",
        "@media (max-width: 360px)": {
            textAlign: "left"
        }
    },
    appointmentCard: {
        "&.MuiCard-root": {
            padding: "10px 20px",
            width: "100%",
            justifyContent: "center",
            display: "flex",
            [theme.breakpoints.down("sm")]: {
                padding: "12px 12px",
                width: 248,
                margin: "30px auto",
            }

        },
    },
    gridContent: {
        justifyContent: "space-between"
    },
    appointmentCardBtn: {
        height: "52px",
    },
    buttonContainer: {
        [theme.breakpoints.down("sm")]: {
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
            marginTop: 10,
        },
        marginTop: 10,
        display: "flex",
        justifyContent: "center",
        flexWrap: "wrap",
    },
}));

interface ISheduleProps {
    getLocation: () => void
}

const Schedule: React.FC<ISheduleProps> = ({getLocation}) => {
    const classes = useStyle();
    const dispatch = useAppDispatch();
    let history = useHistory();
    const theme = useTheme();
    const path = useLocation();
    const {enqueueSnackbar} = useSnackbar();
    const [petsList, setPetsList] = useState([]);
    const partnerId = useSelector((state: RootState) => state?.branding?.partnerId);
    const [profileState, setProfileState] = useState("");
    const [addressId, setAddressId] = useState(null);
    const [service, setService] = useState<serviceType>(partnerId === 13 ? "ADVICE_VIDEO" : "ADVICE_CHAT");
    const [reason, setReason] = useState<reasonType>("ADVICE&TRIAGE");
    const [immediateService, setImmediateService] = useState<immediateType>("IMMEDIATE_CHAT");
    const [desiredDates, setDesiredDates] = useState<desiredDates>("SCHEDULE_LATER");
    const [selectedPet, setSelectedPet] = useState(null);
    const [isTelehealthOnly, setIsTelehealthOnly] = useState(true);
    const [lastAppointment, setLastAppointment] = useState(null);
    const [lastVet, setLastVet] = useState(null);
    const [upcomingApptVet, setUpcomingApptVet] = useState(null);
    const location = useSelector((state: RootState) => state?.common.location);
    const isChanged = useSelector((state: RootState) => state?.common.sessionChanges);
    const user = useSelector((state: RootState) => state?.user);
    const profile = useSelector((state: RootState) => state?.userProfile);
    const isWhiteLabel = useSelector((state: RootState) => state?.branding.partnerPreferencesOn);
    const creditsAvailable = useSelector((state: RootState) => state?.credits.creditsAmount);
    const upcomingAppointments = useSelector((state: RootState) => state?.appointmentList.upcomingAppointments);
    const clientPrices = useSelector((state: RootState) => state?.common?.prices);
    const [prices, setPrices] = useState([]);
    const branding = useSelector((state: RootState) => state?.branding);
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    let alignResonsive: GridItemsAlignment = isMobile ? "flex-start" : "center";
    let justifyResonsive: GridItemsAlignment = "center";
    useUpdateUpcomingAppts();

    useEffect(() => {
        (async () => {
            if (user.role === "VET") {
                history.push("/");
            }
            if (path.state) {
                history.push(`joinRoom${path.state}`);
            }
            if (user.userId) {
                try {
                    const profile = await dispatch(getUserProfile({userId: user.userId}));
                    const response = await API.getAnimal(user.userId);
                    setPetsList(response);
                    if (!profile.length) {
                        enqueueSnackbar("You have no profile yet, you will be redirected to create it in a moment");
                        setTimeout(() => {
                            history.push({
                                pathname: "/dashboardProfile",
                            });
                        }, 3000);
                    } else {
                        if (response.length) {
                            response.length === 1 ? setSelectedPet(response[0].id) : setSelectedPet(null);
                        } else {
                            enqueueSnackbar("There are no pets in your pets list, you will be redirected to edit page of your first pet in a moment");
                            setTimeout(() => {
                                history.push({
                                    pathname: "/pets/0/general",
                                });
                            }, 3000);
                        }
                    }
                    if (!isWhiteLabel) {
                        dispatch(getCreditsExpirationDate());
                        dispatch(getCreditRequestsCount());
                    }
                    await getLocation();
                } catch (err) {
                    enqueueSnackbar(err.errorMessage, {
                        autoHideDuration: 3000,
                        variant: "error"
                    });
                }
            } else {
                document.location.reload();
            }
        })();
    }, []);

    useEffect(() => {
        dispatch(getAddress(user.userId))
            .then((address) => {
                setProfileState(address?.state);
                setAddressId(address?.id);
            });
    }, []);

    useEffect(() => {
        (async () => {
            if (location !== undefined && !clientPrices.adviceVideo.length) {
                const price = await API.getApptPrices(partnerId, location);
                dispatch(setClientPrices(price));
            }
        })();
    },[location]);

    useEffect(() => {
        (async () => {
            checkIfTelehealthOnly(location);
            if (reason === "PRESCRIPTION") {
                setService("RX_VIDEO");
            }
            if (reason === "ADVICE&TRIAGE") setService(partnerId === 13 ? "ADVICE_VIDEO" : "ADVICE_CHAT");
            setPrices(
                reason === "PRESCRIPTION"
                    ? [{
                        type: "RX_VIDEO",
                        label: `Video - $${clientPrices.rxVideo}`,
                        checked: false
                    }]
                    : partnerId === 13
                        ? [{type: "ADVICE_VIDEO", label: "Video", checked: false},]
                        : [
                            {
                                type: "ADVICE_VIDEO",
                                label: `Video ${clientPrices.adviceVideo !== "5" && clientPrices.adviceVideo.length ? "- $" + clientPrices.adviceVideo : ""}`,
                                checked: false
                            },
                            {
                                type: "ADVICE_CHAT",
                                label: `Chat ${clientPrices.adviceChat !== "5" && clientPrices.adviceVideo.length? "- $" + clientPrices.adviceChat : ""}`,
                                checked: false
                            },
                        ]);
        })();
    }, [reason, profile, location, clientPrices]);

    const checkIfTelehealthOnly = (location) => {
        let isTelehealthOnly = !location ? true : statesWithoutPrescription.find(state => state.value === location);
        if (isTelehealthOnly) {
            setIsTelehealthOnly(true);
            setReason("ADVICE&TRIAGE");
        } else {
            setIsTelehealthOnly(false);
        }
    };

    const handleClick = async () => {
        let isAvaliableToMoveForvard = true;
        if (!selectedPet) {
            enqueueSnackbar("Please select a pet!", {variant: "error"});
            isAvaliableToMoveForvard = false;
        }
        if (isAvaliableToMoveForvard) {
            dispatch(updateSelectedPetID(selectedPet));
            if (reason === "PRESCRIPTION") dispatch(updateSelectedState(location));
            dispatch(updateRequestReason(reason));
            switch (desiredDates) {
                case "IMMEDIATELY":
                    dispatch(updateServiceType(immediateService));
                    dispatch(updateIsImmediate(true));
                    history.push("/invoice");
                    break;
                case "SCHEDULE_LATER":
                    dispatch(updateServiceType(service));
                    dispatch(updateIsImmediate(false));
                    dispatch(setSelectedPrice(prices.find(el => el.type === service)?.label.slice(-2).slice(0, 2) || "35"));
                    dispatch(setPrice(prices.find(el => el.type === service)?.label.slice(-2).slice(0, 2) || "35"));
                    history.push({pathname: "/availableVet", state: {redirect: false}});
                    break;
            }
        }
    };

    useEffect(() => {
        api_getLastAppointment()
            .then((res) => {
                setLastAppointment(res);
            });
    }, []);

    useEffect(() => {
        if (upcomingAppointments.length) {
            api_getUserProfile({userId: upcomingAppointments[0].vetUserId})
                .then(([vet]) => {
                    setUpcomingApptVet(vet);
                });
        }
    }, [upcomingAppointments]);

    useEffect(() => {
        if (lastAppointment) {
            api_getUserProfile({userId: lastAppointment.vetUserId})
                .then(([vet]) => {
                    setLastVet(vet);
                });
        }
    }, [lastAppointment]);

    const handlePaymentWithCredits = () => {
        dispatch(updatePaymentMethod("WITH_CREDITS"));
        handleClick();
    };

    const handlePaymentWithoutCredits = () => {
        dispatch(updatePaymentMethod("WITHOUT_CREDITS"));
        dispatch(cleanUpPhotos());
        dispatch(cleanUpVideo());
        handleClick();
    };

    const handleBookAgain = () => {
        dispatch(updateSelectedPetID(lastAppointment.animal.id));
        dispatch(updateVetId(lastAppointment.vetUserId));
        dispatch(updateVetProfile(lastVet));
        dispatch(updateServiceType(lastAppointment.type));
        if (lastAppointment.type === "RX_VIDEO") dispatch(updateRequestReason("PRESCRIPTION"));
        dispatch(cleanUpPhotos());
        dispatch(cleanUpVideo());
        dispatch(setSelectedPrice(prices.find(el => el.type === lastAppointment.type)?.label.slice(-3).slice(0, 2) || "35"));
        dispatch(setPrice(prices.find(el => el.type === lastAppointment.type)?.label.slice(-3).slice(0, 2) || "35"));
        history.push("/bookAgain");
    };

    return (
        <Container maxWidth="md">
            <Box marginTop={isMobile ? "30px" : "100px"} marginBottom="75px" paddingBottom="20px">
                <Grid container>
                    {!isMobile &&
                    <Grid item xs={3}>
                        <Navigation/>
                    </Grid>
                    }

                    <Grid item md={9} xs={12}>
                        <Container>
                            <Grid container direction="column">
                                <Grid item>
                                    <Box marginBottom="59px">
                                        {!isChanged && profileState?.length && location && profileState !== location ? <Card variant="outlined" className={classes.appointmentCard}>

                                            <Grid container alignItems="center">
                                                <Grid container alignItems="center" className={classes.buttonContainer}>
                                                    <WarningSVG/>
                                                    <Typography style={{marginTop: 5}} variant="body2">Your device’s location and the address in your profile do not match. To ensure that you see the correct appointment types, please select the US state where you are located below.</Typography>
                                                </Grid>
                                                <Grid container alignItems="center" className={classes.buttonContainer}>
                                                    <Button style={{marginRight: 5}} onClick={() => {
                                                        dispatch(setLocation(profileState));
                                                        dispatch(setChangesForSession(true));
                                                    }}  variant="contained">{profileState}</Button>
                                                    <Button style={{marginLeft: 5}} onClick={() => {
                                                        dispatch(patchAddress(addressId, {state: location, defaultAddress: true})).then(address => {setProfileState(address?.state);
                                                            setAddressId(address?.id);});
                                                    }}  variant="contained">{location}</Button>
                                                </Grid>
                                            </Grid>
                                        </Card> : null}
                                        <Grid container className={classes.rowContainer} alignItems={alignResonsive}
                                            justify={isMobile ? "center" : "flex-start"}>
                                            <Typography variant="h2">
                                                Schedule Now
                                            </Typography>
                                        </Grid>
                                        <Grid container className={classes.rowContainer} alignItems={alignResonsive}
                                            justify={justifyResonsive}>
                                            <Typography variant="h3" className={classes.sectionText}>
                                                Select your pet
                                            </Typography>
                                        </Grid>
                                        <Grid container className={classes.rowContainer} justify="center"
                                            alignItems={alignResonsive}>
                                            {
                                                petsList.map((pet) => {
                                                    return (
                                                        <Grid xs={6} sm={4} item container justify="center"
                                                            key={pet.id}>
                                                            <Box onClick={() => {
                                                                setSelectedPet(pet.id);
                                                            }}>
                                                                <Avatar variant="square"
                                                                    className={classes.avatar + ` ${selectedPet === pet.id ? classes.selectedPet : ""}`}>
                                                                    {
                                                                        // Using vanilla js object and select by the key, so we could switch i
                                                                        !isWhiteLabel?
                                                                            ({
                                                                                "FELINE": <Image src={pet.imageUrl}
                                                                                    errorIcon={<PetCatSVG/>}
                                                                                    imageStyle={{objectFit: "cover"}}
                                                                                    style={{width: "100%"}}/>,
                                                                                "CANINE": <Image src={pet.imageUrl}
                                                                                    errorIcon={<PetDogSVG/>}
                                                                                    imageStyle={{objectFit: "cover"}}
                                                                                    style={{width: "100%"}}/>,
                                                                                // Any other type
                                                                            }[setPetType(pet.type)] ||
                                                                                <Box display="flex"
                                                                                    justifyContent="center"
                                                                                    alignItems="center" width="100px"
                                                                                    height="100px">
                                                                                    {pet.imageUrl &&
                                                                                    <Image
                                                                                        imageStyle={{objectFit: "cover"}}
                                                                                        src={pet.imageUrl}
                                                                                        errorIcon={<LogoSVG/>}
                                                                                        style={{width: "100%"}}/>}
                                                                                </Box>
                                                                            )
                                                                            :
                                                                            <Box display="flex"
                                                                                justifyContent="center"
                                                                                alignItems="center" width="100px"
                                                                                height="100px">
                                                                                <Image
                                                                                    imageStyle={{objectFit: "cover"}}
                                                                                    src={pet.imageUrl}
                                                                                    errorIcon={<img src={branding.logo} width="100%"/>}
                                                                                    style={{width: "100%"}}/>
                                                                            </Box>
                                                                    }
                                                                </Avatar>
                                                                <Typography style={{
                                                                    overflowX: "hidden",
                                                                    textOverflow: "ellipsis",
                                                                    maxWidth: "95px",
                                                                    whiteSpace: "nowrap"
                                                                }}>
                                                                    {pet.name}
                                                                </Typography>
                                                            </Box>
                                                        </Grid>
                                                    );
                                                })
                                            }
                                        </Grid>
                                        {
                                            (!isWhiteLabel && !isTelehealthOnly &&
                                                <>
                                                    <Grid container className={classes.rowContainer}
                                                        alignItems={alignResonsive}
                                                        justify={justifyResonsive}>
                                                        <Typography variant="h3">
                                                            Select the reason for appointment
                                                        </Typography>
                                                    </Grid>
                                                    <Grid container className={classes.rowContainer}
                                                        alignItems={alignResonsive}
                                                        justify={justifyResonsive}>
                                                        <ServiceType service={reason} setService={setReason}
                                                            items={reasons}/>
                                                    </Grid>
                                                </>)
                                        }
                                        <Grid container className={classes.rowContainer} alignItems={alignResonsive}
                                            justify={justifyResonsive}>
                                            <Typography variant="h3">
                                                Select Appointment Type
                                            </Typography>
                                        </Grid>
                                        <Grid container className={classes.rowContainer} alignItems={alignResonsive}
                                            justify={justifyResonsive}>
                                            <ServiceType service={service} setService={setService}
                                                items={prices}/>
                                            {
                                                !isWhiteLabel &&
                                                <>
                                                    <Typography color="textSecondary" align="center"
                                                        style={{width: "80%"}}>Scheduled
                                                        appointments are with a licensed veterinarian</Typography>
                                                </>
                                            }
                                        </Grid>
                                        {(creditsAvailable && !isWhiteLabel && desiredDates === "IMMEDIATELY") ?
                                            <>
                                                <Grid alignItems={alignResonsive} justify={justifyResonsive} container
                                                    className={classes.rowContainer}>
                                                    <Button color="secondary" variant="contained"
                                                        onClick={handlePaymentWithCredits}
                                                        className={classes.nextButton}>Pay Using Available Credits -
                                                        1
                                                        Credit</Button>
                                                </Grid>
                                                <Grid alignItems={alignResonsive} justify={justifyResonsive} container
                                                    className={classes.rowContainer} style={{marginTop: "10px"}}>
                                                    <Button variant="contained" onClick={handlePaymentWithoutCredits}
                                                        className={classes.nextButton}>Pay for Single Appointment -
                                                        ${setAppointmentPrice(service)}</Button>
                                                </Grid>
                                            </>
                                            :
                                            <Grid alignItems={alignResonsive} justify={justifyResonsive} container
                                                className={classes.rowContainer}>
                                                <Button color="secondary" variant="contained"
                                                    onClick={handlePaymentWithoutCredits}
                                                    className={classes.nextButton}>Book Now</Button>
                                            </Grid>
                                        }
                                        {
                                            isWhiteLabel &&
                                            <Box display="flex" justifyContent="center" alignItems="center"
                                                marginTop="15px">
                                                <Typography color="primary" style={{fontSize: 14, marginRight: "10px"}}>Powered
                                                    by</Typography>
                                                < LogoSVG style={{width: 47}}/>
                                            </Box>
                                        }
                                    </Box>
                                </Grid>
                                <Grid item>
                                    {
                                        //@ts-ignore
                                        !!(upcomingAppointments.length && upcomingApptVet && upcomingAppointments.find(el=> !el.finished && ((new Date() - new Date(el.scheduledDate)) < 3600000 )))
                                            ?
                                            <BookAgainCard
                                                title="Upcoming Appointment"
                                                vetProfile={upcomingApptVet}
                                                //@ts-ignore
                                                appt={upcomingAppointments.find(el=> !el.finished && ((new Date() - new Date(el.scheduledDate)) < 3600000 ))}
                                                upcomingAppt
                                            />
                                            :
                                            ( lastAppointment && lastVet) &&
                                            <BookAgainCard
                                                title="Book Again"
                                                handleNext={handleBookAgain}
                                                nextBtnLabel="Book Again"
                                                appt={lastAppointment}
                                                vetProfile={lastVet}
                                            />
                                    }
                                </Grid>
                            </Grid>
                        </Container>
                    </Grid>
                </Grid>
            </Box>
        </Container>
    );
};

export default withLocation(Schedule);
